// @flow
import React from "react";
import { makeAutoObservable } from "mobx";
import type { Notice } from "../apiv2/entities/notices";
import Binder from "../utils/binder";
import { useToast } from "../components/toast/useToast";
import { socketErrorHandler } from "../utils/sockerErrorHandler";
import apiv2 from "../../src/apiv2";
import { NoticeToast } from "../components/notices/NoticeToast";
import * as moment from "moment";
import styled from "styled-components";
import { DefaultToastBody } from "../components/toast/toast";
import Typography from "@mui/material/Typography";
import StarIcon from "@mui/icons-material/Star";
import { EVALUATION_COLOR_MAP } from "./appointmentEvaluationStore";
import appointmentStore from "./AppointmentStore";

const AnimatedToastBody = styled(DefaultToastBody)`
  animation: pulse 2s infinite ease-in-out;
`;

const toastManager = useToast();

const MessageType = {
  NEW_NOTICE: "new_notice"
};

export const NOTICE_TYPES = {
  INFO: "info",
  ANALYSIS: "analysis",
  QUESTIONNAIRE: "questionnaire",
  LINK: "link",
  IMPORTANT: "important",
  AI_EVALUATION_STARTED: "ai_evaluation_started",
  AI_EVALUATION_FINISHED: "ai_evaluation_finished"
};

export const NOTICE_TYPES_RU = {
  [NOTICE_TYPES.INFO]: "Информация",
  [NOTICE_TYPES.ANALYSIS]: "Анализ",
  [NOTICE_TYPES.QUESTIONNAIRE]: "Анкета",
  [NOTICE_TYPES.LINK]: "Внешняя ссылка",
  [NOTICE_TYPES.IMPORTANT]: "Важное"
};

class NoticesStore {
  socket = null;
  notices = [];
  noticesHeader = [];
  // unreadNotices = [];
  isInitialized = false;

  constructor() {
    Binder.bind(this, NoticesStore);
    makeAutoObservable(this);
  }

  async initialize() {
    if (!this.isInitialized) {
      await this.fetchUnreadAndReadNotices();
      this.isInitialized = true;
      if (this.unreadNoticesCount) {
        const isImportantUnreadNotices = !!this.importantUnreadNotices.length;
        const title = isImportantUnreadNotices ? "У Вас есть важные непрочитанные уведомления" : "У Вас есть непрочитанные уведомления"
        toastManager.showToast(<NoticeToast title={title} modifiers={[isImportantUnreadNotices ? "flickering" : "" ]}/>, {
          duration: 10000,
          container: {
            component: isImportantUnreadNotices ? AnimatedToastBody : DefaultToastBody
          },
        });
      }
    }
  }

  initWS() {
    this.socket = new WebSocket(apiv2.notices.getSocketUrl().toString());

    this.socket.onopen = () => {
      console.info("connection to notices channel established");
    };

    this.socket.onclose = socketErrorHandler(this.socket, () => this.initWS());

    this.socket.onmessage = event => {
      const message = JSON.parse(event.data);

      // Сделал еще один switch-case так как сообщения для оценки приемов имеют тот же message.type
      switch (message.type) {
        case MessageType.NEW_NOTICE: {
          switch (message.data.type) {
            case NOTICE_TYPES.AI_EVALUATION_STARTED: {
              toastManager.showToast(
                <NoticeToast title={message.data.title} text={message.data.description} />,
                {
                  duration: 10000,
                  container: {
                    component: DefaultToastBody
                  }
                }
              );

              break;
            }
            case NOTICE_TYPES.AI_EVALUATION_FINISHED: {
              const text = "Результат: ";
              const colorIcon =
                EVALUATION_COLOR_MAP[message.data.params?.ai_evaluation?.total_color ?? null];
              const appointmentId = message.data.params?.appointment_id;

              const description = (
                <Typography component="span">
                  {text}
                  <StarIcon
                    sx={{ color: colorIcon, verticalAlign: "middle", width: 16, height: 16 }}
                  />
                </Typography>
              );

              toastManager.showToast(
                <NoticeToast title={message.data.title} customDescription={description} />,
                {
                  duration: 10000,
                  container: {
                    component: DefaultToastBody
                  }
                }
              );

              if (appointmentId) {
                appointmentStore.getAppointmentById(appointmentId, true);
              }

              break;
            }
            default: {
              const isImportantUnreadNotices = message.data.type === NOTICE_TYPES.IMPORTANT;
              const title = isImportantUnreadNotices
                ? "У Вас новое важное уведомление"
                : "У Вас новое уведомление";
              toastManager.showToast(<NoticeToast title={title} text={message.data.title} />, {
                duration: 10000,
                container: {
                  component: isImportantUnreadNotices ? AnimatedToastBody : DefaultToastBody
                }
              });

              break;
            }
          }

          /** В сообщении нет полного тела уведомления, пришлось перезапрашиваться */
          this.fetchUnreadAndReadNotices();
          break;
        }
        default:
          break;
      }
    };
  }

  get readedNotices() {
    return this.noticesHeader
      .filter(notice => notice.is_read)
      .sort((a, b) => moment(b.created_at) - moment(a.created_at));
  }

  get unreadNotices() {
    return this.noticesHeader.filter(
      notice => !notice.is_read && notice.type !== NOTICE_TYPES.IMPORTANT
    );
  }

  get importantUnreadNotices() {
    return this.noticesHeader.filter(
      notice => !notice.is_read && notice.type === NOTICE_TYPES.IMPORTANT
    );
  }

  get unreadNoticesCount() {
    return this.unreadNotices.length + this.importantUnreadNotices.length;
  }

  async createNotice(noticeData: Omit<Notice, "id">) {
    try {
      return await apiv2.notices.createNotice(noticeData);
    } catch (e) {
      console.log(e);
    }
  }

  async fetchAllNotices() {
    try {
      let response = await apiv2.notices.getNotices();
      if (response) {
        this.notices = response;
      }
      return response;
    } catch (e) {
      console.log(e);
    }
  }

  async fetchUnreadNotices() {
    try {
      let response = await apiv2.notices.getUnreadNotices();
      if (response) {
        // TODO: Вернуть если будем использовать их получение таким способом
        // this.unreadNotices = response;
      }
      return response;
    } catch (e) {
      console.log(e);
    }
  }

  async fetchUnreadAndReadNotices() {
    try {
      let response = await apiv2.notices.getUnreadAndReadNotices();
      if (response) {
        this.noticesHeader = response;
      }
    } catch (e) {
      console.log(e);
    }
  }

  async readNotice(noticeId) {
    try {
      const response = await apiv2.notices.readNotice(noticeId);
      this.noticesHeader = this.noticesHeader.map(notice => {
        if (notice.id === noticeId) {
          notice.is_read = true;
        }
        return notice;
      });
      return response;
    } catch (e) {
      console.log(e);
    }
  }

  async makeNoticeUnread(noticeId) {
    try {
      return await apiv2.notices.unreadNotice(noticeId);
    } catch (e) {
      console.log(e);
    }
  }

  async editNotice(noticeId: Number, noticeData: Notice) {
    try {
      return await apiv2.notices.updateNotice(noticeId, noticeData);
    } catch (e) {
      console.log(e);
    }
  }

  async deleteNotice(noticeId) {
    try {
      return await apiv2.notices.deleteNotice(noticeId);
    } catch (e) {
      console.log(e);
    }
  }
}

export default new NoticesStore();
